import * as yup from 'yup';
import { PatientPortalDataAction } from './patient-portal-data';
import { isDayNotGreaterThan } from '../utils/formatter';

export enum HistoryType {
    PastMedicalHistory = 0,
    SocialHistory = 2,
    FamilyHistory = 3,
    SmokingHistory = 4,
    DrugHistory = 5,
    FallHistory = 6,
    DrinkingHistory = 7
}

export enum PatientHistoryType {
    PastMedicalHistory = 0,
    SocialHistory = 2,
    FamilyHistory = 3,
    SmokingHistory = 4,
    DrugHistory = 5,
    FallHistory = 6,
    DrinkingHistory = 7
}

export class PatientHistoryModel {
    id: number = 0;

    historyType: PatientHistoryType = PatientHistoryType.PastMedicalHistory;

    description?: string;
    code: number = 0;
    icd10Code?: string;
    historyDateFrom?: string;
    historyDateTo?: string;
    yearFrom?: number;
    yearTo?: number;

    // for family history
    familyHistoryRelationship?: number;
    familyHistoryAgeOnset?: number;
    familyHistoryIsDeceased?: boolean;
    familyHistoryCauseOfDeath?: boolean;

    createdDate: string = '';

    constructor(
        type: PatientHistoryType,
        code?: number
    ) {
        this.historyType = type;

        if (code) {
            this.code = code;
        }
    }
}

export class PatientHistory {
    id: number = 0;
    impsId: number = 0;

    historyType: HistoryType = HistoryType.PastMedicalHistory;

    description: Undefinable<string>;
    code: number = 0;
    icd10Code: Undefinable<string>;
    historyDateFrom: Undefinable<string>;
    historyDateTo: Undefinable<string>;
    yearFrom: Undefinable<number>;
    yearTo: Undefinable<number>;

    // for family history
    familyHistoryRelationship: Undefinable<number>;
    familyHistoryAgeOnset: Undefinable<number>;
    familyHistoryIsDeceased: Undefinable<boolean>;
    familyHistoryCauseOfDeath: Undefinable<boolean>;

    createdDate: string = '';

    constructor(type: HistoryType, code?: number) {
        this.historyType = type;

        if (code) {
            this.code = code;
        }
    }
}

export interface PatientHistoryExtend extends PatientHistory {
    relatedId: Undefinable<string>;
    action: Undefinable<PatientPortalDataAction>;
}

export const fullDateSet = new Set([HistoryType.PastMedicalHistory, HistoryType.SocialHistory]);
export const yearDateSet = new Set([
    HistoryType.SmokingHistory,
    HistoryType.DrugHistory,
    HistoryType.FallHistory,
    HistoryType.DrinkingHistory
]);

export const patientHistoryValidationSchema = yup.object().shape(
    {
        code: yup
            .number()
            .when(
                'historyType',
                {
                    is: (value: HistoryType) =>
                        value !== HistoryType.SmokingHistory &&
                        value !== HistoryType.DrinkingHistory &&
                        value !== HistoryType.PastMedicalHistory &&
                        value !== HistoryType.FamilyHistory
                    ,
                    then: yup
                        .number()
                        .moreThan(0, 'History name is required.')
                        .required('History name is required.')
                }
            ),
        icd10Code: yup
            .string()
            .nullable()
            .when(
                'historyType',
                {
                    is: (value: HistoryType) => value === HistoryType.PastMedicalHistory || value === HistoryType.FamilyHistory,
                    then: yup
                        .string()
                        .required('History name is required.')
                }
            ),
        historyDateFrom: yup
            .string()
            .nullable()
            .when(
                ['historyType', 'historyDateTo'],
                (
                    historyType: HistoryType,
                    historyDateTo: Undefinable<string>,
                    schema: yup.Schema<string>
                ) => {
                    if (fullDateSet.has(historyType)) {
                        return schema.test(
                            'history_date_from',
                            'From date must be equal or less than To date',
                            (historyDateFrom: Undefinable<string>) => {
                                return isDayNotGreaterThan(historyDateFrom, historyDateTo, 'MM/DD/YYYY');
                            }
                        );
                    }
                }
            ),
        historyDateTo: yup
            .string()
            .nullable()
            .when(
                ['historyType', 'historyDateFrom'],
                (
                    historyType: HistoryType,
                    historyDateFrom: Undefinable<string>,
                    schema: yup.Schema<string>
                ) => {
                    if (fullDateSet.has(historyType)) {
                        return schema.test(
                            'history_date_to',
                            'To date must be equal or greater than From date',
                            (historyDateTo: Undefinable<string>) => {
                                return isDayNotGreaterThan(historyDateFrom, historyDateTo, 'MM/DD/YYYY');
                            }
                        );
                    }
                }
            ),
        yearFrom: yup
            .number()
            .nullable()
            .when(
                ['historyType', 'yearTo'],
                (
                    historyType: HistoryType,
                    yearTo: Undefinable<number>,
                    schema: yup.Schema<string>
                ) => {
                    if (yearDateSet.has(historyType)) {
                        return schema.test(
                            'year_from',
                            'From date must be equal or less than To date',
                            (yearFrom: Undefinable<number>) => {
                                return isDayNotGreaterThan((yearFrom || '').toString(), (yearTo || '').toString());
                            }
                        );
                    }
                }
            ),
        yearTo: yup
            .number()
            .nullable()
            .when(
                ['historyType', 'yearFrom'],
                (
                    historyType: HistoryType,
                    yearFrom: Undefinable<number>,
                    schema: yup.Schema<string>
                ) => {
                    if (yearDateSet.has(historyType)) {
                        return schema.test(
                            'year_to',
                            'From date must be equal or less than To date',
                            (yearTo: Undefinable<number>) => {
                                return isDayNotGreaterThan((yearFrom || '').toString(), (yearTo || '').toString());
                            }
                        );
                    }
                }
            ),
        familyHistoryRelationship: yup
            .number()
            .nullable()
            .when(
                'historyType',
                {
                    is: (value: HistoryType) => value === HistoryType.FamilyHistory,
                    then: yup.number()
                        .moreThan(0, 'Family member is required.')
                        .required('Family member is required.'),
                }
            )
    },
    [['historyDateFrom', 'historyDateTo'], ['yearFrom', 'yearTo']]
);
